import type { ReactNode } from "react";
import { ChevronRightIcon, PlusIcon } from "lucide-react";

import { cn } from "~/lib/utils";

export interface PlanningGroupHeaderProps {
  readonly label: string;
  readonly count: number;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  /** Status / priority glyph shown before the label. */
  readonly leading?: ReactNode;
  /** Optional quick-add control (e.g. new task in this status). */
  readonly onCreate?: (() => void) | undefined;
  readonly createLabel?: string | undefined;
  readonly className?: string;
}

/**
 * Linear-like planning group bar: soft rounded surface, leading glyph, label,
 * muted count, collapse chevron, optional trailing +.
 */
export function PlanningGroupHeader(props: PlanningGroupHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-sm",
        props.className,
      )}
    >
      <button
        type="button"
        onClick={props.onToggle}
        aria-expanded={props.expanded}
        className="flex min-h-touch min-w-0 flex-1 items-center gap-1.5 font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-0"
      >
        <ChevronRightIcon
          className={cn(
            "size-3 shrink-0 transition-transform duration-150",
            props.expanded ? "rotate-90" : null,
          )}
          aria-hidden="true"
        />
        {props.leading != null ? (
          <span className="inline-flex shrink-0 items-center" aria-hidden="true">
            {props.leading}
          </span>
        ) : null}
        <span className="truncate text-foreground/80">{props.label}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground/60">{props.count}</span>
      </button>
      {props.onCreate != null ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            props.onCreate?.();
          }}
          aria-label={props.createLabel ?? `Add in ${props.label}`}
          className="inline-flex size-touch shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:size-5"
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}
